import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';

import defaultImg from '../../asset/project/default_1.png';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { WorkDetailSimpleData } from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { IMPORT_STATION_DETAIL_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';
import { resolveUploadFileOriginUrl } from '../../shared/util/normalizeWorkData';
import { shrinkString } from '../../shared/util/shrinkString';

import BlockWithVisible from './BlockWithVisible';

interface SimpleWorkDetailForPEProps {
  workId: string;
  data: WorkDetailSimpleData;
  showArtist: boolean;
  artistName: string;
}

const SimpleWorkDetailForPE: React.FC<SimpleWorkDetailForPEProps> = ({
  workId,
  data,
  showArtist,
  artistName,
}) => {
  const { addSelectedData, selectedDataList, simpleList } = useImportStationWorkStore();
  const { projectElementList } = useProjectElementState();
  const { enqueueAlert } = useStandardAlertStore();
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const detailOriginUrl = resolveUploadFileOriginUrl(data.uploadFile);
  const imageUrl = useOptimizedImageUrl(detailOriginUrl, imgContainerRef);

  const detailIdsInProject = useMemo(
    () =>
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.DETAIL)
        .map((pe) => (pe as { workDetail: { id: string } }).workDetail.id),
    [projectElementList]
  );
  const isAlreadyInProject = detailIdsInProject.includes(data.id);
  const isSelected = selectedDataList.some((s) => s.workId === workId && s.detailId === data.id);
  const shrinkedDescription = useMemo(
    () => shrinkString(data.description, IMPORT_STATION_DETAIL_DESCRIPTION_MAX_LENGTH),
    [data.description],
  );

  const onClickHandler = () => {
    if (isAlreadyInProject) return;
    if (selectedDataList.length >= BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT }),
      });
      return;
    }
    const parentWork = simpleList.find((work) => work.id === workId);
    addSelectedData({
      workId,
      detailId: data.id,
      originUrl: detailOriginUrl,
      title: parentWork?.title,
      prodYear: parentWork?.prodYear,
      material: parentWork?.material,
      size: parentWork?.size,
      sizeText: parentWork?.sizeText,
      dimensionsType: parentWork?.dimensionsType,
      duration: parentWork?.duration,
      artist: parentWork?.artist,
    });
  };

  return (
    <SimpleWorkDetailForPEComp
      key={data.id}
      $isSelected={isSelected}
      $isAlreadyInProject={isAlreadyInProject}
      onClick={() => onClickHandler()}
    >
      <ImageBlock>
        <Indicatior>{'└'}</Indicatior>
        <ImgWrapper ref={imgContainerRef}>
          <WorkImage src={imageUrl === '' ? defaultImg : imageUrl} alt="work detail" />
        </ImgWrapper>
      </ImageBlock>
      {showArtist && (
        <ArtistBlock>
          <span>{artistName}</span>
        </ArtistBlock>
      )}
      <TitleBlock title={data.description}>{shrinkedDescription}</TitleBlock>
      <BlockWithVisible width={'1.6'} value={data.detailType} />
      <BtnWrapper>-</BtnWrapper>
    </SimpleWorkDetailForPEComp>
  );
};

const SimpleWorkDetailForPEComp = styled.div<{ $isSelected?: boolean; $isAlreadyInProject?: boolean }>`
  width: 100%;
  height: 50px;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in-out;
  opacity: ${({ $isSelected, $isAlreadyInProject }) => ($isAlreadyInProject ? 0.4 : $isSelected ? 0.5 : 1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  cursor: ${({ $isSelected, $isAlreadyInProject }) =>
    $isAlreadyInProject ? 'default' : $isSelected ? 'not-allowed' : 'pointer'};
`;

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 10px;
`;

const Indicatior = styled.div`
  width: 10%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 20px;
  ${({ theme }) => theme.typography.Body_04};
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 80%;
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleBlock = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnWrapper = styled.div`
  flex: 0.2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.Body_04};
`;

export default SimpleWorkDetailForPE;
